import * as React from "react";

import { cn } from "../../lib/utils";

const FormFieldContext = React.createContext({
  id: "",
  errorId: "",
  required: false,
  describedByIds: [],
  registerDescribedBy: () => {},
});

const FormBody = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("form-body", className)} {...props} />
));
FormBody.displayName = "FormBody";

const FormGrid = React.forwardRef(({ className, cols = 2, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "grid grid-cols-1 gap-3 md:gap-2",
      cols === 2 && "md:grid-cols-2",
      className
    )}
    {...props}
  />
));
FormGrid.displayName = "FormGrid";

const FormField = React.forwardRef(({ className, span = 1, id: idProp, required = false, children, ...props }, ref) => {
  const generatedId = React.useId();
  const id = idProp || generatedId;
  const errorId = `${id}-error`;
  const [describedByIds, setDescribedByIds] = React.useState([]);
  const registerDescribedBy = React.useCallback((descId) => {
    if (!descId) return;
    setDescribedByIds((prev) => (prev.includes(descId) ? prev : [...prev, descId]));
  }, []);

  return (
    <FormFieldContext.Provider value={{ id, errorId, required, describedByIds, registerDescribedBy }}>
      <div
        ref={ref}
        className={cn("form-field", span === 2 && "md:col-span-2", className)}
        {...props}
      >
        {children}
      </div>
    </FormFieldContext.Provider>
  );
});
FormField.displayName = "FormField";

const FormLabel = React.forwardRef(
  ({ className, required: requiredProp, children, htmlFor, ...props }, ref) => {
    const { id, required: requiredFromContext } = React.useContext(FormFieldContext);
    const required = requiredProp ?? requiredFromContext;

    return (
      <label
        ref={ref}
        htmlFor={htmlFor ?? (id || undefined)}
        className={cn("form-label", className)}
        {...props}
      >
        {children}
        {required ? (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
    );
  }
);
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef(({ className, children, ...props }, ref) => {
  const { id, required, describedByIds } = React.useContext(FormFieldContext);
  const describedBy = describedByIds.length > 0 ? describedByIds.join(" ") : undefined;

  if (!React.isValidElement(children)) {
    return children;
  }

  return React.cloneElement(children, {
    ref,
    id: children.props.id ?? id,
    className: cn(children.props.className, className),
    "aria-describedby": children.props["aria-describedby"] ?? describedBy,
    "aria-required": children.props["aria-required"] ?? (required ? true : undefined),
    required: children.props.required ?? (required ? true : undefined),
    ...props,
  });
});
FormControl.displayName = "FormControl";

const FormHint = React.forwardRef(({ className, id: idProp, ...props }, ref) => {
  const { id, registerDescribedBy } = React.useContext(FormFieldContext);
  const hintId = idProp ?? (id ? `${id}-hint` : undefined);

  React.useLayoutEffect(() => {
    registerDescribedBy(hintId);
  }, [hintId, registerDescribedBy]);

  return (
    <p
      ref={ref}
      id={hintId}
      className={cn("form-hint", className)}
      {...props}
    />
  );
});
FormHint.displayName = "FormHint";

const FormError = React.forwardRef(({ className, children, id: idProp, ...props }, ref) => {
  const { errorId, registerDescribedBy } = React.useContext(FormFieldContext);
  const resolvedErrorId = idProp ?? (errorId || undefined);

  React.useLayoutEffect(() => {
    if (children) {
      registerDescribedBy(resolvedErrorId);
    }
  }, [children, resolvedErrorId, registerDescribedBy]);

  if (!children) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={resolvedErrorId}
      role="alert"
      className={cn("form-error", className)}
      {...props}
    >
      {children}
    </p>
  );
});
FormError.displayName = "FormError";

const FormFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("form-footer", className)} {...props} />
));
FormFooter.displayName = "FormFooter";

export {
  FormBody,
  FormGrid,
  FormField,
  FormLabel,
  FormControl,
  FormHint,
  FormError,
  FormFooter,
};
